import React, { useState, useEffect, useCallback, useMemo } from "react";
import EmptyListState from "../../components/EmptyListState/EmptyListState.jsx";
import FullWidthAsyncButton from "../../components/FullWidthButton/FullWidthAsyncButton";
import { useNavigate, useParams } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import auth from "../../fire.js";
import axiosClient from "../../axiosClient";
import WorkoutTemplateCard from "../../components/WorkoutTemplateCard/WorkoutTemplateCard.jsx";
import DeleteModal from "../../components/DeleteModal/DeleteModal.jsx";
import BasicInput from "../../components/BasicInput/BasicInput.jsx";
import PreviousPageHeader from "../../components/PreviousPageHeader/PreviousPageHeader.jsx";
import MessageModal from "../../components/MessageModal/MessageModal.jsx";
import TextSkeleton from "../../components/SkeletonComponents/TextSkeleton";
import { ClipLoader } from "react-spinners";
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from "react-beautiful-dnd";
import { get, set } from "lodash";

const WorkoutTemplates: React.FC = () => {
  const { programId } = useParams();
  const navigate = useNavigate();
  const [user, loading] = useAuthState(auth);
  const [program, setProgram] = useState<Program | null>(null);
  const [workoutTemplates, setWorkoutTemplates] = useState<WorkoutTemplate[]>(
    [],
  );
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [workoutTemplateToDelete, setWorkoutTemplateToDelete] = useState<
    string | null
  >(null);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [modalState, setModalState] = useState({
    isOpen: false,
    type: "",
    title: "",
    message: "",
  });
  const [isWorkoutTemplatesLoading, setIsWorkoutTemplatesLoading] =
    useState(false);
  // Should refactor to update on screen resize
  const blurbRef = React.useRef<HTMLParagraphElement>(null);
  const { innerHeight: screenHeight } = window;

  const getProgram = useCallback(async () => {
    try {
      const getProgramResponse = await axiosClient.get(
        `/api/programs/${programId}`,
        {
          params: { populateWorkoutTemplates: true },
        },
      );
      const program = getProgramResponse.data;
      const workoutTemplates = program.workoutTemplates;

      setProgram(program);
      setWorkoutTemplates(workoutTemplates);
      setIsInitialLoading(false);
    } catch (e: any) {
      console.error(e);
      const errorMessage =
        e?.response?.data?.error || "Something unexpected occured.";
      setModalState({
        isOpen: true,
        type: "error",
        title: "Error Fetching Program",
        message: `${errorMessage} Please try again or contact support.`,
      });
      setIsInitialLoading(false);
    }
  }, [programId]);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate("/login");
      return;
    }

    getProgram();
  }, [user, loading, navigate, getProgram]);

  const handleProgramTitleSubmit = async (title: String) => {
    try {
      const updateProgramResponse = await axiosClient.put(
        `/api/programs/${programId}`,
        { title: title || "Untitled Program" },
      );

      if (updateProgramResponse.status !== 200) {
        throw new Error(updateProgramResponse.data.message);
      }
    } catch (e: any) {
      console.error(e);
      const errorMessage =
        e?.response?.data?.error || "Something unexpected occured.";
      setModalState({
        isOpen: true,
        type: "error",
        title: "Error Saving Program Name",
        message: `${errorMessage} Please try again or contact support.`,
      });
      //Maybe refresh the page here? Or replace input value with title before update?
    }
  };

  const handleNewWorkoutTemplate = async () => {
    const newWorkoutTemplate = {
      name: "Untitled Program Workout",
      exercises: [],
      notes: "",
      program: programId,
    };

    try {
      const createWorkoutTemplateResponse = await axiosClient.post(
        `/api/workout-templates/`,
        newWorkoutTemplate,
      );

      if (createWorkoutTemplateResponse.status !== 201) {
        throw new Error(createWorkoutTemplateResponse.data.message);
      }

      const createdWorkoutTemplate = createWorkoutTemplateResponse.data;

      navigate(
        `/programs/${programId}/workout-templates/${createdWorkoutTemplate._id}`,
      );
    } catch (e: any) {
      console.error(e);
      const errorMessage =
        e?.response?.data?.error || "Something unexpected occured.";
      setModalState({
        isOpen: true,
        type: "error",
        title: "Error Creating Program Workout",
        message: `${errorMessage} Please try again or contact support.`,
      });
    }
  };

  const handleDeleteWorkoutTemplate = async () => {
    setIsDeleteModalOpen(false);

    if (workoutTemplates) {
      const filteredWorkoutTemplates = workoutTemplates.filter(
        (workoutTemplate) => workoutTemplate._id !== workoutTemplateToDelete,
      );
      setWorkoutTemplates(filteredWorkoutTemplates);
    }

    try {
      const deleteWorkoutTemplateResponse = await axiosClient.delete(
        `/api/workout-templates/${workoutTemplateToDelete}`,
      );

      if (deleteWorkoutTemplateResponse.status !== 200) {
        throw new Error(deleteWorkoutTemplateResponse.data.message);
      }
    } catch (e: any) {
      console.error(e);
      const errorMessage =
        e?.response?.data?.error || "Something unexpected occured.";
      setModalState({
        isOpen: true,
        type: "error",
        title: "Error Deleting Program",
        message: `${errorMessage} Please try again or contact support.`,
      });
      setIsInitialLoading(true);
      getProgram();
    }

    setWorkoutTemplateToDelete(null);
  };

  const handleDuplicateWorkoutTemplate = async (workoutTemplateId: String) => {
    setIsWorkoutTemplatesLoading(true);
    try {
      const duplicateWorkoutTemplateResponse = await axiosClient.post(
        `/api/workout-templates/duplicate-workout-template/`,
        {
          templateId: workoutTemplateId,
        },
      );

      if (duplicateWorkoutTemplateResponse.status !== 201) {
        throw new Error();
      }

      const newDuplicateWorkoutTemplate = duplicateWorkoutTemplateResponse.data;
      setWorkoutTemplates((prevWorkoutTemplates) => [
        ...prevWorkoutTemplates,
        newDuplicateWorkoutTemplate,
      ]);
      setIsWorkoutTemplatesLoading(false);
    } catch (e: any) {
      console.error(e);
      const errorMessage =
        e?.response?.data?.error || "Something unexpected occured.";
      setModalState({
        isOpen: true,
        type: "error",
        title: "Error Duplicating Program Workout",
        message: `${errorMessage} Please try again or contact support.`,
      });
      setIsWorkoutTemplatesLoading(false);
    }
  };

  const handleWorkoutTemplateDragAndDrop = async (results: DropResult) => {
    const { source, destination } = results;

    if (!destination) return;
    if (source.index === destination.index) return;

    const reorderedWorkoutTemplates = [...workoutTemplates];
    const [movedWorkoutTemplate] = reorderedWorkoutTemplates.splice(
      source.index,
      1,
    );
    reorderedWorkoutTemplates.splice(
      destination.index,
      0,
      movedWorkoutTemplate,
    );

    setWorkoutTemplates(reorderedWorkoutTemplates);

    try {
      const updateProgramResponse = await axiosClient.put(
        `/api/programs/${programId}`,
        { workoutTemplates: reorderedWorkoutTemplates },
      );

      if (updateProgramResponse.status !== 200) {
        throw new Error(updateProgramResponse.data.message);
      }
    } catch (e: any) {
      console.error(e);
      const errorMessage =
        e?.response?.data?.error || "Something unexpected occured.";
      setModalState({
        isOpen: true,
        type: "error",
        title: "Error Moving Workout Template",
        message: `${errorMessage} Please try again or contact support.`,
      });

      setIsInitialLoading(true);
      getProgram();
    }
  };

  const LOGO_HEADER = 63;
  const BLURB = blurbRef.current?.clientHeight || 24;
  const PROGRAMS_TITLE_INPUT = 70;
  const WORKOUTS_LIST_TITLE = 48;
  const NEW_WORKOUT_BTN = 70;
  const MAIN_NAV = 63;
  const OTHER_CONTENT_HEIGHT =
    LOGO_HEADER +
    BLURB +
    PROGRAMS_TITLE_INPUT +
    WORKOUTS_LIST_TITLE +
    NEW_WORKOUT_BTN +
    MAIN_NAV;

  const amountOfCardSkeletons = useMemo(() => {
    const listHeight = screenHeight - OTHER_CONTENT_HEIGHT;
    return Math.ceil(listHeight / 62);
  }, [OTHER_CONTENT_HEIGHT, screenHeight]);

  return (
    <div className="h-full relative">
      <PreviousPageHeader previousPageLink={`/programs`} pageTitle="Program" />
      <p ref={blurbRef} className="p-2 pb-0 text-xs font-regular text-gray-500">
        Name your program and add workouts to your personalized plan.
      </p>
      <div className="pt-2 px-2">
        {isInitialLoading ? (
          <>
            <label className="block text-sm font-medium text-gray-700">
              {"Name"}
            </label>
            <TextSkeleton height="38px" className="w-full mt-1" />
          </>
        ) : (
          <BasicInput
            id="program-title"
            label="Name"
            initialValue={
              program?.title === "Untitled Program" ? "" : program?.title
            }
            onBlur={handleProgramTitleSubmit}
            placeholder="Push/Pull/Legs, 5x5, Full Body, etc."
          />
        )}
      </div>
      <div className="pt-4 pb-2 px-2 flex flex-row gap-x-2">
        <h2 className=" text-base font-medium text-gray-900">Workouts</h2>
        {isWorkoutTemplatesLoading && (
          <ClipLoader
            color="indigo-500"
            speedMultiplier={0.6}
            size={"1.25rem"}
          />
        )}
      </div>
      {isInitialLoading ? (
        <>
          <div
            className="px-2 overflow-hidden"
            style={{ height: `calc(100vh - ${OTHER_CONTENT_HEIGHT}px)` }}
          >
            {Array.from({ length: amountOfCardSkeletons }, (number, index) => (
              <TextSkeleton key={index} height="62px" className="w-full mb-1" />
            ))}
          </div>
        </>
      ) : (
        <div
          className="px-2 pr-0 overflow-y-scroll"
          style={{ height: `calc(100vh - ${OTHER_CONTENT_HEIGHT}px)` }}
        >
          {workoutTemplates && workoutTemplates.length === 0 && (
            <EmptyListState
              title="No Program Workouts"
              message="Create a new workout to get started."
              fullscreenList
            />
          )}
          {workoutTemplates && workoutTemplates.length > 0 && (
            <DragDropContext onDragEnd={handleWorkoutTemplateDragAndDrop}>
              <Droppable droppableId="ROOT" type="group">
                {(provided) => (
                  <div {...provided.droppableProps} ref={provided.innerRef}>
                    {workoutTemplates.map((workoutTemplate, index) => {
                      return (
                        <Draggable
                          draggableId={workoutTemplate._id}
                          key={workoutTemplate._id}
                          index={index}
                        >
                          {(provided) => (
                            <div
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              ref={provided.innerRef}
                              className="mb-1"
                            >
                              <WorkoutTemplateCard
                                className={"mr-2"}
                                key={workoutTemplate._id}
                                {...workoutTemplate}
                                onOpenDeleteWorkoutTemplateModal={() => {
                                  setWorkoutTemplateToDelete(
                                    workoutTemplate._id,
                                  );
                                  setIsDeleteModalOpen(true);
                                }}
                                onDuplicate={handleDuplicateWorkoutTemplate}
                              />
                            </div>
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          )}
        </div>
      )}
      <div className="py-4 px-2 w-full bg-gray-50 absolute bottom-0">
        <FullWidthAsyncButton
          text="New Program Workout"
          loadingText="Creating Program Workout"
          icon="PlusIcon"
          onClick={handleNewWorkoutTemplate}
          isSkeleton={isInitialLoading}
        />
      </div>
      <MessageModal
        isOpen={modalState.isOpen}
        onModalClose={() => {
          setModalState({ ...modalState, isOpen: false });
        }}
        type={modalState.type}
        title={modalState.title}
        message={modalState.message}
      />
      <DeleteModal
        isOpen={isDeleteModalOpen}
        onModalClose={() => {
          setIsDeleteModalOpen(false);
          setWorkoutTemplateToDelete(null);
        }}
        onSubmit={handleDeleteWorkoutTemplate}
        deleteTitle={"Delete Program Workout"}
        deleteMessage={
          "Are you sure you want to delete? This Program Workout and all its exercises will be permanently removed. This action cannot be undone."
        }
      />
    </div>
  );
};

export default WorkoutTemplates;
